import { App } from '@capacitor/app';
import { isNative } from './platform.js';
import { navigate } from '../runtime/legacy.js';
import { APP_SCHEME, DEEP_LINK_HOSTS } from '../config/env.js';

/**
 * App lifecycle and deep links.
 *
 * A wrapped web view is suspended, not unloaded, when the user switches away.
 * Coming back after an hour, the Supabase access token has usually expired and
 * every request 401s until something refreshes it — which on the web is handled
 * by a page reload that never happens here. So: refresh the session on resume,
 * and persist any in-progress workout on suspend.
 */

const SESSION_REFRESH_AFTER_MS = 5 * 60 * 1000;

let backgroundedAt = 0;

async function refreshSessionIfStale() {
  const client = globalThis.supabaseClient;
  if (!client || globalThis.isDemoMode) return;

  const awayFor = Date.now() - backgroundedAt;
  if (backgroundedAt === 0 || awayFor < SESSION_REFRESH_AFTER_MS) return;

  try {
    const { data, error } = await client.auth.getSession();
    if (error) throw error;
    // getSession() refreshes on its own when the token is close to expiry; an
    // absent session after a refresh attempt means the user is genuinely
    // signed out, and the router should send them to the landing page.
    if (!data?.session && globalThis.AppState?.isAuthenticated) {
      globalThis.AppState.isAuthenticated = false;
      navigate('/');
    }
  } catch (err) {
    console.warn('[ironpact] session refresh on resume failed', err);
  }
}

function persistActiveWorkout() {
  try {
    globalThis.saveActiveWorkoutToStorage?.();
  } catch (err) {
    console.warn('[ironpact] could not persist active workout', err);
  }
}

/**
 * Maps an incoming universal/app link onto a hash route.
 * `ironpact://workout/new` and `https://<host>/#/workout/new` both land on
 * `#/workout/new`.
 */
function routeFromDeepLink(url) {
  try {
    const parsed = new URL(url);
    if (parsed.hash) return parsed.hash.slice(1);
    if (parsed.protocol === `${APP_SCHEME}:`) {
      return `/${parsed.hostname}${parsed.pathname}`.replace(/\/+$/, '') || '/';
    }
    if (DEEP_LINK_HOSTS.includes(parsed.hostname)) return parsed.pathname || '/';
  } catch {
    /* malformed link — ignore rather than navigating somewhere unexpected */
  }
  return null;
}

export function installLifecycle() {
  // The web equivalents matter for the installed PWA, so they are not gated on
  // `isNative`.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      backgroundedAt = Date.now();
      persistActiveWorkout();
    } else {
      refreshSessionIfStale();
    }
  });

  if (!isNative) return;

  App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) refreshSessionIfStale();
    else {
      backgroundedAt = Date.now();
      persistActiveWorkout();
    }
  });

  App.addListener('appUrlOpen', ({ url }) => {
    const route = routeFromDeepLink(url);
    if (route) navigate(route);
  });
}
